import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchPredictionReadiness, fetchPredictionAccuracy,
  fetchUpcomingPredictions, fetchAllocation, triggerPredictionRun,
} from '../api/client';
import RankingTable from '../components/RankingTable';
import useWindowWidth from '../hooks/useWindowWidth';

const cardStyle = {
  background: 'var(--surf)', borderRadius: 16,
  boxShadow: 'var(--shadow)', padding: 20,
};
const num = (v) => (v || 0).toLocaleString('he-IL');

// The three components are deliberately not equal: volume of data never
// proves a forecast works, so accuracy carries the most weight and the score
// stays capped until predictions have actually been scored.
const COMPONENT_NOTE = {
  history: 'נצבר מעצמו — כל יום של לכידה מוסיף. זה הרכיב שאי אפשר לזרז.',
  features: 'הרכיב היחיד שאפשר לקפוץ בו מיד: השלמת קרדיטים והפעלת מקורות באזז.',
  accuracy: 'עולה רק כשתחזיות שנרשמו מראש מנוקדות מול התוצאה בפועל.',
};

const PART_LABEL = {
  curve_days: 'ימי עקומת מכירה',
  frozen_labels: 'תוצאות קפואות',
  trainable_screenings: 'הקרנות בנות-אימון',
  complete_movies: 'סרטים עם מחזור מלא',
  credits_coverage: 'סרטים עם קרדיטים',
  buzz_sources: 'מקורות באזז פעילים',
  buzz_days: 'ימי באזז',
  context_days: 'ימי מזג אוויר/חגים',
  scored: 'תחזיות שנוקדו',
  skill: 'שגיאה ממוצעת (MAPE %)',
};

function ReadinessBar({ score, milestones, cappedAt }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 44, fontWeight: 800, color: 'var(--accent)',
                      fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
          {pct}%
        </div>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          מוכנות לתחזית כרטיסים מדויקת
        </div>
      </div>

      <div style={{ position: 'relative', height: 14, borderRadius: 8,
                    background: 'var(--surf-alt)', overflow: 'visible' }}>
        <div style={{ height: '100%', borderRadius: 8, background: 'var(--accent)',
                      width: `${pct}%`, transition: 'width .4s' }} />
        {/* The cap is a real ceiling, not decoration: with zero scored
            predictions the accuracy component contributes nothing. */}
        {cappedAt < 100 && (
          <div style={{ position: 'absolute', top: 0, bottom: 0, right: `${cappedAt}%`,
                        left: 0, background: 'repeating-linear-gradient(45deg,'
                        + 'transparent,transparent 5px,var(--faint) 5px,var(--faint) 6px)',
                        borderRadius: 8, opacity: 0.35, pointerEvents: 'none' }} />
        )}
        {(milestones || []).map((m) => (
          <div key={m.at} style={{ position: 'absolute', top: -4, bottom: -4,
                                   right: `${m.at}%`, width: 2,
                                   background: pct >= m.at ? 'var(--accent)' : 'var(--brd)' }} />
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 8 }}>
        {(milestones || []).map((m) => (
          <div key={m.at} style={{ flex: 1, fontSize: 10,
                                   color: pct >= m.at ? 'var(--accent)' : 'var(--faint)' }}>
            <div style={{ fontWeight: 700 }}>{m.at}% · {m.label}</div>
            <div style={{ color: 'var(--faint)' }}>{m.note}</div>
          </div>
        ))}
      </div>

      {cappedAt < 100 && (
        <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 10, lineHeight: 1.6 }}>
          חסום ב-{cappedAt}% עד שיצטברו תחזיות מנוקדות. כמות נתונים לבדה אינה מוכיחה
          שהתחזית עובדת — רק השוואה לתוצאה בפועל מוכיחה.
        </div>
      )}
    </div>
  );
}

function ComponentCard({ comp }) {
  const parts = Object.entries(comp.parts || {});
  return (
    <div style={{ ...cardStyle, flex: '1 1 280px', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{comp.label}</div>
        <div style={{ fontSize: 11, color: 'var(--faint)' }}>משקל {comp.weight}%</div>
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--txt)',
                    fontVariantNumeric: 'tabular-nums', margin: '4px 0 10px' }}>
        {comp.score}%
      </div>
      <div style={{ height: 5, borderRadius: 4, background: 'var(--surf-alt)',
                    overflow: 'hidden', marginBottom: 12 }}>
        <div style={{ height: '100%', borderRadius: 4, background: 'var(--accent)',
                      width: `${Math.max(0, Math.min(100, comp.score))}%` }} />
      </div>

      {parts.map(([key, p]) => {
        const ratio = Math.round((p.score || 0) * 100);
        return (
          <div key={key} style={{ marginBottom: 7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          fontSize: 11, color: 'var(--muted)' }}>
              <span>{PART_LABEL[key] || key}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', direction: 'ltr' }}>
                {p.value === null ? '—' : num(p.value)}
                {p.target ? ` / ${num(p.target)}` : ''}
              </span>
            </div>
            <div style={{ height: 3, borderRadius: 2, background: 'var(--surf-alt)',
                          overflow: 'hidden', marginTop: 3 }}>
              <div style={{ height: '100%', background: 'var(--faint)', width: `${ratio}%` }} />
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 10, lineHeight: 1.6 }}>
        {COMPONENT_NOTE[comp.key] || comp.hint}
      </div>
    </div>
  );
}

function PredictionsPage({ onMovieClick, refreshNonce }) {
  const [readiness, setReadiness] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const mob = useWindowWidth() < 768;

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchPredictionReadiness().then(setReadiness),
      fetchPredictionAccuracy().then(setAccuracy).catch(() => setAccuracy(null)),
      fetchUpcomingPredictions(40).then(setUpcoming).catch(() => setUpcoming(null)),
      fetchAllocation(14, 7).then(setAllocation).catch(() => setAllocation(null)),
    ])
      .catch(() => setError('שגיאה בטעינת נתוני התחזית'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshNonce]);

  if (loading && !readiness) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--faint)' }}>טוען תחזיות...</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Experimental banner — repeated in the nav, because a page of
          numbers reads as authoritative unless told otherwise. */}
      <div style={{
        background: 'var(--accent-bg)', border: '1px solid var(--accent)',
        borderRadius: 12, padding: '12px 16px', fontSize: 12, lineHeight: 1.7,
      }}>
        <strong>עמוד ניסיוני — עדיין לא מוכן.</strong> אין כאן מודל מאומן. התחזיות
        שמוצגות הן אקסטרפולציה פשוטה ממכירה מוקדמת, והן נרשמות בעיקר כדי שאפשר יהיה
        למדוד כמה הן שגויות. <strong>אין להסתמך עליהן להחלטות.</strong>
      </div>

      {error && <div style={{ ...cardStyle, color: 'var(--danger)' }}>{error}</div>}

      {readiness && (
        <div style={cardStyle}>
          <ReadinessBar
            score={readiness.score}
            milestones={readiness.milestones}
            cappedAt={readiness.capped_at}
          />
        </div>
      )}

      {readiness && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {readiness.components.map((c) => <ComponentCard key={c.key} comp={c} />)}
        </div>
      )}

      {/* Manual trigger. The scheduled job runs every 3 hours, which is far
          too slow a feedback loop to tell "found nothing to do" apart from
          "never ran" — the exact ambiguity that let it sit idle unnoticed. */}
      <div style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap',
                    alignItems: 'center', gap: 12 }}>
        <button
          onClick={() => {
            setRunning(true);
            setRunResult(null);
            triggerPredictionRun()
              .then((res) => { setRunResult(res); load(); })
              .catch((e) => setRunResult({
                error: e?.response?.data?.detail || 'הרצה נכשלה',
              }))
              .finally(() => setRunning(false));
          }}
          disabled={running}
          style={{
            padding: '9px 18px', borderRadius: 10, border: 'none',
            background: running ? 'var(--surf-alt)' : 'var(--accent)',
            color: running ? 'var(--faint)' : '#fff', fontWeight: 700,
            fontSize: 13, cursor: running ? 'default' : 'pointer',
          }}
        >
          {running ? 'מריץ...' : 'הרץ סבב תחזיות עכשיו'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7 }}>
          רושם תחזיות להקרנות עתידיות ומנקד תחזיות שההקרנה שלהן כבר הסתיימה.
          רץ גם אוטומטית כל 3 שעות.
        </div>
        {runResult && (
          <div style={{ width: '100%', fontSize: 12, direction: 'rtl',
                        color: runResult.error ? 'var(--danger)' : 'var(--ok)' }}>
            {runResult.error
              ? runResult.error
              : `נרשמו ${num(runResult.created)} תחזיות, דולגו ${num(runResult.skipped)}, נוקדו ${num(runResult.scored)}.`}
          </div>
        )}
      </div>

      {/* Accuracy — the component that unlocks the rest of the bar. */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>דיוק מול המציאות</div>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.7 }}>
          כל תחזית נרשמת <strong>לפני</strong> ההקרנה ומנוקדת אחריה מול התוצאה הקפואה.
          פילוח לפי אופק כי תחזית לשעתיים קדימה ותחזית לשבועיים קדימה הן שתי בעיות שונות.
        </p>
        {!accuracy || accuracy.total_scored === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--faint)', fontSize: 12 }}>
            עדיין לא נוקדה אף תחזית.
            {accuracy?.pending ? ` ${num(accuracy.pending)} תחזיות ממתינות לתוצאה.` : ''}
            <div style={{ marginTop: 6 }}>
              המדד יופיע אחרי שהקרנות שנחזו יסתיימו והתוצאה שלהן תיסגר.
            </div>
          </div>
        ) : (
          <RankingTable
            columns={[
              { key: 'horizon', header: 'אופק', align: 'right' },
              { key: 'scored', header: 'נוקדו', sortValue: (r) => r.scored },
              { key: 'mape', header: 'שגיאה ממוצעת %', sortValue: (r) => r.mape ?? 999,
                render: (r) => (r.mape === null ? '—' : `${r.mape}%`) },
              { key: 'bias', header: 'הטיה %', sortValue: (r) => r.bias ?? 0,
                render: (r) => (r.bias === null ? '—' : (
                  <span style={{ color: Math.abs(r.bias) < 5 ? 'var(--ok)' : 'var(--warn)' }}>
                    {r.bias > 0 ? '+' : ''}{r.bias}%
                  </span>
                )) },
              { key: 'mae', header: 'שגיאה בכרטיסים', sortValue: (r) => r.mae ?? 0 },
              { key: 'avg_actual', header: 'ממוצע בפועל', sortValue: (r) => r.avg_actual ?? 0 },
            ]}
            data={accuracy.by_horizon}
            pageSize={10}
            minWidth={560}
          />
        )}
      </div>

      {/* Allocation — the one strong signal that already works today. */}
      {allocation?.movies?.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            הקצאת הקרנות — התחזית של הרשתות עצמן
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.7 }}>
            כמה הקרנות רשת מקצה לסרט, באילו אולמות ובאילו שעות, זו הערכת הביקוש שלה
            מגובה בכסף — מאנשים שיודעים את תקציב השיווק ואת הביצועים בחו״ל. זמין ימים
            לפני שנמכר הכרטיס הראשון, ובלי שום תלות במקור חיצוני.
            <br />
            טווח: {allocation.window.from} עד {allocation.window.to}
          </p>
          <RankingTable
            columns={[
              { key: 'title', header: 'סרט', align: 'right', mobileWide: true },
              { key: 'screenings', header: 'הקרנות', sortValue: (r) => r.screenings },
              { key: 'screen_share', header: 'נתח מהלוח', sortValue: (r) => r.screen_share,
                render: (r) => `${r.screen_share}%` },
              { key: 'cinemas', header: 'סניפים', sortValue: (r) => r.cinemas },
              { key: 'chains', header: 'רשתות', sortValue: (r) => r.chains },
              { key: 'avg_hall_size', header: 'אולם ממוצע', sortValue: (r) => r.avg_hall_size },
              { key: 'prime_time_share', header: 'פריים-טיים', sortValue: (r) => r.prime_time_share,
                render: (r) => `${r.prime_time_share}%` },
              { key: 'premium_screenings', header: 'פרימיום', sortValue: (r) => r.premium_screenings },
            ]}
            data={allocation.movies}
            rankColumn
            pageSize={15}
            minWidth={720}
            onRowClick={(r) => r.movie_id && onMovieClick && onMovieClick(r.movie_id)}
          />
        </div>
      )}

      {/* Raw baseline output, last: it is the least trustworthy thing here. */}
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
          תחזיות בסיס להקרנות קרובות
        </div>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.7 }}>
          שיטת <code>presale_v1</code>: תפוסה נוכחית ועוד התוספת החציונית שנמדדה
          בהקרנות סגורות באותו אופק. הטווח הוא פס גס ולא רווח סמך סטטיסטי.
        </p>
        {!upcoming || upcoming.predictions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--faint)', fontSize: 12 }}>
            אין עדיין תחזיות. הן נוצרות אוטומטית כל 3 שעות להקרנות עתידיות.
          </div>
        ) : (
          <RankingTable
            columns={[
              { key: 'movie', header: 'סרט', align: 'right', mobileWide: true },
              { key: 'cinema', header: 'סניף', align: 'right' },
              { key: 'showtime', header: 'מועד', sortValue: (r) => r.showtime },
              { key: 'horizon', header: 'אופק' },
              { key: 'sold_now', header: 'נמכרו', sortValue: (r) => r.sold_now },
              { key: 'predicted', header: 'תחזית', sortValue: (r) => r.predicted,
                render: (r) => (
                  <span style={{ fontWeight: 700 }}>
                    {Math.round(r.predicted)}
                    <span style={{ color: 'var(--faint)', fontSize: 10, marginRight: 4 }}>
                      ({Math.round(r.lower)}–{Math.round(r.upper)})
                    </span>
                  </span>
                ) },
              { key: 'total_seats', header: 'קיבולת', sortValue: (r) => r.total_seats },
            ]}
            data={upcoming.predictions}
            pageSize={mob ? 8 : 15}
            minWidth={760}
            onRowClick={(r) => r.movie_id && onMovieClick && onMovieClick(r.movie_id)}
          />
        )}
      </div>
    </div>
  );
}

export default PredictionsPage;
